import { useMemo, useCallback, useEffect } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Form, Row, Col, Input, Label, Button, FormGroup } from "reactstrap";

const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const PropertySelector = ({
    propertyList,
    selectedFilters,
    onPropertySelectionChange,
    onAddClick,
    isLoading,
}) => {
    const currentProperty = useMemo(() =>
        propertyList.find(prop => prop._id === selectedFilters.property_id),
        [propertyList, selectedFilters.property_id]
    );

    const buildingList = useMemo(() => {
        if (!currentProperty?.building) return [];
        return currentProperty.building
            .filter(b => b && b.buildingName)
            .map(b => b.buildingName);
    }, [currentProperty]);

    const isAddButtonDisabled = useMemo(() => {
        const hasPropertyId = Boolean(selectedFilters.property_id);
        const hasBuildingName = Boolean(selectedFilters.building_name);

        if (buildingList.length > 0 && !hasBuildingName) {
            return true;
        }

        return !hasPropertyId;
    }, [selectedFilters.property_id, selectedFilters.building_name, buildingList.length]);

    const propertyOptions = useMemo(() =>
        propertyList.map(prop => ({
            value: prop._id,
            label: `${prop.title}, ${prop.location}`
        })),
        [propertyList]
    );

    const buildingOptions = useMemo(() =>
        buildingList.map(building => ({
            value: building,
            label: building
        })),
        [buildingList]
    );

    // '' sends no month, so the server applies no date filter.
    const monthOptions = [
        { value: '', label: 'All months' },
        ...months.map((month, index) => ({
            value: String(index + 1),
            label: month
        })),
    ];

    const selectedPropertyOption = useMemo(() =>
        propertyOptions.find(option => option.value === selectedFilters.property_id) || null,
        [propertyOptions, selectedFilters.property_id]
    );

    const selectedBuildingOption = useMemo(() =>
        buildingOptions.find(option => option.value === selectedFilters.building_name) || null,
        [buildingOptions, selectedFilters.building_name]
    );

    const selectedMonthOption = useMemo(() =>
        monthOptions.find(option => option.value === selectedFilters.month) || null,
        [monthOptions, selectedFilters.month]
    );

    useEffect(() => {
        if (buildingList.length === 1 && !selectedFilters.building_name && selectedFilters.property_id) {
            onPropertySelectionChange({
                ...selectedFilters,
                building_name: buildingList[0]
            });
        }
    }, [buildingList, selectedFilters, onPropertySelectionChange]);

    useEffect(() => {
        if (currentProperty && selectedFilters.property_id) {
            const updatedFilters = {
                ...selectedFilters,
                address: currentProperty.address || selectedFilters.address || '',
                postal_code: currentProperty.postal_code || selectedFilters.postal_code || '',
            };

            if (updatedFilters.address !== selectedFilters.address ||
                updatedFilters.postal_code !== selectedFilters.postal_code) {
                onPropertySelectionChange(updatedFilters);
            }
        }
    }, [currentProperty, selectedFilters, onPropertySelectionChange]);

    const handlePropertyChange = useCallback((selectedOption) => {
        const newPropertyId = selectedOption ? selectedOption.value : '';
        const selectedProp = propertyList.find(prop => prop._id === newPropertyId);

        onPropertySelectionChange({
            ...selectedFilters,
            property_id: newPropertyId,
            building_name: '',
            address: selectedProp?.address || '',
            postal_code: selectedProp?.postal_code || ''
        });
    }, [onPropertySelectionChange, propertyList, selectedFilters]);

    const handleBuildingChange = useCallback((selectedOption) => {
        const newBuildingName = selectedOption ? selectedOption.value : '';

        onPropertySelectionChange({
            ...selectedFilters,
            building_name: newBuildingName
        });
    }, [selectedFilters, onPropertySelectionChange]);

    const handleMonthChange = useCallback((selectedOption) => {
        const newMonth = selectedOption ? selectedOption.value : '';

        onPropertySelectionChange({
            ...selectedFilters,
            month: newMonth
        });
    }, [selectedFilters, onPropertySelectionChange]);

    const handleStatusChange = useCallback((e) => {
        const newStatus = e.target.value;

        onPropertySelectionChange({
            ...selectedFilters,
            status: newStatus
        });
    }, [selectedFilters, onPropertySelectionChange]);

    const customSelectStyles = {
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999
        }),
        control: (base) => ({
            ...base,
            zIndex: 2,
            backgroundColor: buildingList.length === 1 ? '#f8f9fa' : base.backgroundColor,
        })
    };

    return (
        <Form className='mb-3'>
            <Row className="mb-3">
                <Col md={8}>
                    <div className="d-flex flex-wrap gap-4">
                        <FormGroup check inline>
                            <Input
                                type="radio"
                                name="status"
                                value="all"
                                checked={selectedFilters.status === 'all'}
                                onChange={handleStatusChange}
                                id="allFilter"
                            />
                            <Label check for="allFilter" className="fw-medium">
                                All
                            </Label>
                        </FormGroup>

                        <FormGroup check inline>
                            <Input
                                type="radio"
                                name="status"
                                value="needs_work"
                                checked={selectedFilters.status === 'needs_work'}
                                onChange={handleStatusChange}
                                id="needsWorkFilter"
                            />
                            <Label check for="needsWorkFilter" className="text-warning fw-medium">
                                <FeatherIcon icon="alert-triangle" size={16} className="me-1" />
                                Needs Work
                            </Label>
                        </FormGroup>

                        <FormGroup check inline>
                            <Input
                                type="radio"
                                name="status"
                                value="all_good"
                                checked={selectedFilters.status === 'all_good'}
                                onChange={handleStatusChange}
                                id="allGoodFilter"
                            />
                            <Label check for="allGoodFilter" className="text-success fw-medium">
                                <FeatherIcon icon="check-circle" size={16} className="me-1" />
                                All Good
                            </Label>
                        </FormGroup>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label>Property</Label>
                        <Select
                            value={selectedPropertyOption}
                            onChange={handlePropertyChange}
                            options={propertyOptions}
                            placeholder="Select Property"
                            isClearable
                            name="property_id"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={customSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            isDisabled={isLoading}
                        />
                    </FormGroup>
                </Col>

                {buildingList.length > 0 && (
                    <Col md={3}>
                        <FormGroup>
                            <Label>Building Name</Label>
                            <Select
                                value={selectedBuildingOption}
                                onChange={handleBuildingChange}
                                options={buildingOptions}
                                placeholder="Select Building"
                                isClearable={buildingList.length > 1}
                                isDisabled={buildingList.length === 1 || isLoading}
                                name="building_name"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </FormGroup>
                    </Col>
                )}

                <Col md={3}>
                    <FormGroup>
                        <Label>Month</Label>
                        <Select
                            value={selectedMonthOption}
                            onChange={handleMonthChange}
                            options={monthOptions}
                            placeholder="Select Month"
                            isClearable={false}
                            isDisabled={isLoading}
                            name="month"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={customSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                    </FormGroup>
                </Col>

                <Col md={2} className="d-flex align-items-end">
                    <Button
                        color="primary"
                        className="ms-2"
                        onClick={onAddClick}
                        disabled={isAddButtonDisabled || isLoading}
                    >
                        <FeatherIcon icon="plus-circle" size={18} style={{ marginRight: '8px' }} />
                        Generate Log
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export { PropertySelector };